// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useOutletContext, Link } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import helpers from '@/utils/helpers';

import useTimeSlotStore from '@store/timeSlotStore';
import useGameStore from '@store/gameStore';
import useDivisionStore from '@store/divisionStore';
import useTeamDivisionStore from '@store/teamDivisionStore';
import useLeagueStore from '@store/leagueStore';
import useLocationStore from '@store/locationStore';
import { shallow } from 'zustand/shallow';
import { axios } from '@store/axios';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, CalendarDays, Upload } from 'lucide-react';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import QuickAvailabilityWizard from '@/components/QuickAvailabilityWizard/QuickAvailabilityWizard';
import getLocationDisplayName from '@staty/shared/utils/getLocationDisplayName';
import getDefaultGameDuration from '@staty/shared/utils/getDefaultGameDuration';
import { UNASSIGNED_LANE_ID, appendUnassignedLane, isRealLocationId } from '@containers/DivisionPage/scheduleLanes';
import { parseTimeToMinutes, formatMinutesForGameTime, formatTimeLabel, toDateKey, toGlobalDate, isParseableTime } from '@staty/shared/utils/gameDateTime';

const SNAP_MINUTES = 15;
const SLOT_HEIGHT = 22;
const TIME_GUTTER_WIDTH = 80;
const COLUMN_WIDTH = 260;
const DEFAULT_DURATION = 60;
const MAX_VISIBLE_COLUMNS = 3;
const GAME_GAP = 4;
const GAME_INSET = 4;
const baseUrl = `${process.env.API_URL}/api/v1`;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const formatDateForSelect = (dateKey: string) => {
  if (!dateKey) return '';
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateKey;
  return parsed.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getLocationIdFromGame = (game: any, locations: any[]) => {
  const byModel = helpers.getObjId(game.locationModel);
  if (byModel) return byModel;

  const gameLocationObjId = helpers.getObjId(game.location);
  if (gameLocationObjId) return gameLocationObjId;

  if (typeof game.location === 'string') {
    const byName = locations.find(loc =>
      loc?.name === game.location ||
      getLocationDisplayName(loc) === game.location
    );
    if (byName) return byName._id;
  }

  return null;
};

const getTimeSlotForGame = (timeSlots: any[], dateKey: string, locationId: string, startMinutes: number) => {
  return timeSlots.find((slot) => {
    const slotDateKey = toDateKey(slot.availableDate);
    if (slotDateKey !== dateKey) return false;
    if (helpers.getObjId(slot.location) !== locationId) return false;
    const start = parseTimeToMinutes(slot.startTime);
    const end = parseTimeToMinutes(slot.endTime);
    return startMinutes >= start && startMinutes < end;
  });
};

const getTeamName = (teamRef: any, teams: any[]) => {
  const teamId = helpers.getObjId(teamRef);
  if (!teamId) return 'TBD';
  const found = teams.find((t) => t._id === teamId);
  return found?.name || 'TBD';
};

const buildOverlapLayout = (
  games: any[],
  draftByGameId: Record<string, { start: number; duration: number }>,
  defaultDuration: number = DEFAULT_DURATION,
) => {
  const events = games
    .map((game) => {
      const draft = draftByGameId[game._id];
      const start = draft?.start ?? parseTimeToMinutes(game.time);
      const duration = draft?.duration ?? (game.duration || defaultDuration);
      const end = start + duration;
      return { game, start, duration, end };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const groups: { start: number; end: number; events: any[] }[] = [];
  let current: { start: number; end: number; events: any[] } | null = null;

  events.forEach((event) => {
    if (!current || event.start >= current.end) {
      if (current) groups.push(current);
      current = { start: event.start, end: event.end, events: [event] };
      return;
    }

    current.events.push(event);
    current.end = Math.max(current.end, event.end);
  });

  if (current) groups.push(current);

  const items: any[] = [];
  const groupInfo: { start: number; end: number; hiddenCount: number; columnCount: number }[] = [];

  groups.forEach((group) => {
    const columnsEnd: number[] = [];
    const groupItems = group.events.sort((a, b) => a.start - b.start || a.end - b.end);
    const laidOut: any[] = [];

    groupItems.forEach((event) => {
      let columnIndex = columnsEnd.findIndex((end) => end <= event.start);
      if (columnIndex === -1) {
        columnIndex = columnsEnd.length;
        columnsEnd.push(event.end);
      } else {
        columnsEnd[columnIndex] = event.end;
      }

      laidOut.push({
        ...event,
        column: columnIndex,
        columnCount: 0,
        groupStart: group.start,
      });
    });

    const columnCount = columnsEnd.length || 1;
    let hiddenCount = 0;

    laidOut.forEach((item) => {
      item.columnCount = columnCount;
      if (item.column >= MAX_VISIBLE_COLUMNS) hiddenCount += 1;
    });

    items.push(...laidOut);

    groupInfo.push({
      start: group.start,
      end: group.end,
      hiddenCount,
      columnCount,
    });
  });

  return { items, groups: groupInfo };
};

export default function TournamentSchedulerDay2Admin() {
  const { tournamentId } = useParams();
  const { leagueName, league: organization } = useOutletContext() as any;
  const { getToken } = useAuth();

  const calendarRef = useRef<HTMLDivElement | null>(null);

  const [highlightSeasonId, setHighlightSeasonId] = useState('all');
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [allTournamentGames, setAllTournamentGames] = useState<any[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [draftByGameId, setDraftByGameId] = useState<Record<string, { start: number; duration: number; locationId?: string }>>({});
  const [interaction, setInteraction] = useState<any>(null);
  const draftByGameIdRef = useRef<Record<string, { start: number; duration: number; locationId?: string }>>({});

  const [createForm, setCreateForm] = useState({
    seasonId: '',
    divisionId: '',
    homeTeamId: '',
    awayTeamId: '',
    locationId: '',
    startMinutes: 9 * 60,
    duration: DEFAULT_DURATION,
  });

  const { timeSlots, getTimeSlotsByLeagueName, getTimeSlotsByTournamentId } = useTimeSlotStore();
  const { divisions, getActiveDivisionsByTournamentId } = useDivisionStore();
  // Sport-aware default game length (pickleball = 30, else 60). Sport is
  // single-sourced from organization.sports[0] (the outlet-context org).
  const defaultDuration = useMemo(() => getDefaultGameDuration(organization?.sports?.[0]), [organization]);
  const { teamDivisions, getTeamDivisionsByDivisionId } = useTeamDivisionStore();
  const { seasons, getContestsByTournamentId } = useLeagueStore((state: any) => ({
    seasons: state.leagues,
    getContestsByTournamentId: state.getContestsByTournamentId,
  }), shallow);
  const { locations: allLocations, getLocationsByLeagueName } = useLocationStore((state: any) => ({
    locations: state.locations,
    getLocationsByLeagueName: state.getLocationsByLeagueName,
  }), shallow);
  const addGameFn = useGameStore((state: any) => state.addGame);
  const updateGameFn = useGameStore((state: any) => state.updateGame);
  const addGame = useMemo(() => addGameFn(getToken), [addGameFn, getToken]);
  const updateGame = useMemo(() => updateGameFn(getToken), [updateGameFn, getToken]);

  useEffect(() => {
    if (tournamentId) {
      getTimeSlotsByTournamentId(tournamentId);
      getContestsByTournamentId(tournamentId);
      getActiveDivisionsByTournamentId(tournamentId);
      return;
    }
    if (leagueName) {
      getTimeSlotsByLeagueName(leagueName);
    }
  }, [tournamentId, leagueName, getTimeSlotsByLeagueName, getTimeSlotsByTournamentId, getContestsByTournamentId, getActiveDivisionsByTournamentId]);

  useEffect(() => {
    if (!leagueName) return;
    getLocationsByLeagueName(leagueName, { includeSubVenues: true });
  }, [leagueName, getLocationsByLeagueName]);

  // INT-0027 quick availability (tournament): slots for the whole event in
  // one dialog; drafted contest games without a time get placed.
  const [quickOpen, setQuickOpen] = useState(false);
  const timelessGames = useMemo(
    () => allTournamentGames.filter((g) => !isParseableTime(g?.time)).map((g) => ({
      id: String(g._id), dateKey: toDateKey(g.globalDate || g.date), round: g?.competitionMeta?.round, gameNumber: g?.competitionMeta?.gameNumber,
    })),
    [allTournamentGames],
  );

  const loadAllTournamentGames = useCallback(async (seasonList: any[]) => {
    if (!seasonList?.length) {
      setAllTournamentGames([]);
      return;
    }

    const responses = await Promise.all(
      seasonList.map((season) => axios.get(`${baseUrl}/games/season/${season._id}?include=homeTeam,awayTeam,organization,league,division`))
    );

    const merged = responses
      .flatMap((response) => response?.data?.result || [])
      .map((game) => ({
        ...game,
        isHidden: game.isHidden ? 'YES' : 'NO',
        isCancelled: game.isCancelled ? 'YES' : 'NO',
      }));

    const deduped = Array.from(new Map(merged.map((game) => [game._id, game])).values());
    setAllTournamentGames(deduped);
  }, []);

  useEffect(() => {
    if (seasons.length > 0 && !createForm.seasonId) {
      const firstSeasonId = seasons[0]._id;
      setCreateForm((prev) => ({ ...prev, seasonId: firstSeasonId }));
    }
  }, [seasons, createForm.seasonId]);

  useEffect(() => {
    loadAllTournamentGames(seasons);
  }, [seasons, loadAllTournamentGames]);

  useEffect(() => {
    if (divisions.length === 0) return;
    divisions.forEach((division) => {
      getTeamDivisionsByDivisionId(division._id);
    });
  }, [divisions]);

  const allDateKeys = useMemo(() => {
    const dateKeys = new Set<string>();
    timeSlots.forEach((slot) => {
      const key = toDateKey(slot.availableDate);
      if (key) dateKeys.add(key);
    });
    allTournamentGames.forEach((game) => {
      const key = toDateKey(game.globalDate || game.date);
      if (key) dateKeys.add(key);
    });

    return Array.from(dateKeys).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  }, [timeSlots, allTournamentGames]);

  useEffect(() => {
    if (!selectedDateKey && allDateKeys.length > 0) {
      setSelectedDateKey(allDateKeys[0]);
    }
  }, [allDateKeys, selectedDateKey]);

  const locationMatchList = useMemo(() => {
    if (allLocations.length > 0) return allLocations;
    const map = new Map<string, any>();
    timeSlots.forEach((slot) => {
      const locationId = helpers.getObjId(slot.location);
      if (locationId && slot.location) {
        map.set(locationId, slot.location);
      }
    });
    return Array.from(map.values());
  }, [allLocations, timeSlots]);

  const locations = useMemo(() => {
    const map = new Map<string, any>();
    const byId = new Map<string, any>();
    const subVenuesByParent = new Map<string, any[]>();

    locationMatchList.forEach((loc) => {
      const id = helpers.getObjId(loc);
      if (!id) return;
      byId.set(id, loc);
      const parentId = helpers.getObjId(loc.parentLocation);
      if (parentId) {
        if (!subVenuesByParent.has(parentId)) subVenuesByParent.set(parentId, []);
        subVenuesByParent.get(parentId).push(loc);
      }
    });

    const usedParentIds = new Set<string>();
    const parentHasGames = new Set<string>();

    timeSlots.forEach((slot) => {
      const slotDateKey = toDateKey(slot.availableDate);
      if (slotDateKey !== selectedDateKey) return;
      const locationId = helpers.getObjId(slot.location);
      if (!locationId) return;
      const loc = byId.get(locationId) || slot.location;
      const parentId = helpers.getObjId(loc?.parentLocation);
      if (parentId) {
        map.set(locationId, loc);
      } else {
        usedParentIds.add(locationId);
      }
    });

    allTournamentGames.forEach((game) => {
      const gameDateKey = toDateKey(game.globalDate || game.date);
      if (gameDateKey !== selectedDateKey) return;
      const modelId = getLocationIdFromGame(game, locationMatchList);
      if (modelId) {
        const existing = map.get(modelId);
        if (!existing) {
          const loc = byId.get(modelId);
          if (loc) {
            const parentId = helpers.getObjId(loc.parentLocation);
            if (parentId) {
              map.set(modelId, loc);
            } else {
              usedParentIds.add(modelId);
              parentHasGames.add(modelId);
            }
          } else {
            map.set(modelId, { _id: modelId, name: game.location || 'Location' });
          }
        }
      } else if (typeof game.location === 'string') {
        const key = `name:${game.location}`;
        if (!map.has(key)) {
          map.set(key, { _id: key, name: game.location });
        }
      }
    });

    usedParentIds.forEach((parentId) => {
      const subVenues = subVenuesByParent.get(parentId) || [];
      if (subVenues.length > 0) {
        subVenues.forEach((subVenue) => {
          map.set(helpers.getObjId(subVenue), subVenue);
        });
        if (parentHasGames.has(parentId)) {
          const parent = byId.get(parentId);
          if (parent) map.set(parentId, parent);
        }
      } else {
        const parent = byId.get(parentId);
        if (parent) map.set(parentId, parent);
      }
    });

    const ordered = Array.from(map.values());
    ordered.sort((a, b) => {
      const aParent = helpers.getObjId(a.parentLocation);
      const bParent = helpers.getObjId(b.parentLocation);
      const aParentName = aParent
        ? (locationMatchList.find((loc) => helpers.getObjId(loc) === aParent)?.name || '')
        : (a.name || '');
      const bParentName = bParent
        ? (locationMatchList.find((loc) => helpers.getObjId(loc) === bParent)?.name || '')
        : (b.name || '');
      const parentCompare = aParentName.localeCompare(bParentName);
      if (parentCompare !== 0) return parentCompare;
      const aIsSub = Boolean(aParent);
      const bIsSub = Boolean(bParent);
      if (aIsSub !== bIsSub) return aIsSub ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });

    const gamesOnDay = allTournamentGames.filter(
      (game) => toDateKey(game.globalDate || game.date) === selectedDateKey,
    );
    return appendUnassignedLane(ordered, gamesOnDay, (g) => getLocationIdFromGame(g, locationMatchList));
  }, [allTournamentGames, selectedDateKey, timeSlots, locationMatchList]);

  const gamesForDay = useMemo(() => {
    return allTournamentGames.filter((game) => toDateKey(game.globalDate || game.date) === selectedDateKey);
  }, [allTournamentGames, selectedDateKey]);

  const bounds = useMemo(() => {
    let min = 9 * 60;
    let max = 19 * 60;

    timeSlots.forEach((slot) => {
      const slotDateKey = toDateKey(slot.availableDate);
      if (slotDateKey !== selectedDateKey) return;
      min = Math.min(min, parseTimeToMinutes(slot.startTime));
      max = Math.max(max, parseTimeToMinutes(slot.endTime));
    });

    gamesForDay.forEach((game) => {
      const start = parseTimeToMinutes(game.time);
      const duration = game.duration || defaultDuration;
      min = Math.min(min, start);
      max = Math.max(max, start + duration);
    });

    min = clamp(Math.floor((min - 30) / SNAP_MINUTES) * SNAP_MINUTES, 0, 24 * 60 - SNAP_MINUTES);
    max = clamp(Math.ceil((max + 30) / SNAP_MINUTES) * SNAP_MINUTES, SNAP_MINUTES, 24 * 60);

    if (max - min < 8 * 60) {
      max = clamp(min + 8 * 60, SNAP_MINUTES, 24 * 60);
    }

    return { start: min, end: max };
  }, [gamesForDay, selectedDateKey, timeSlots]);

  const totalHeight = ((bounds.end - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;

  const teamsForCreateDivision = useMemo(() => {
    if (!createForm.divisionId) return [];
    return teamDivisions.filter((team) => helpers.getObjId(team.division) === createForm.divisionId);
  }, [teamDivisions, createForm.divisionId]);

  const gamesByLocation = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    locations.forEach((location) => {
      grouped[location._id] = [];
    });

    gamesForDay.forEach((game) => {
      const draft = draftByGameId[game._id];
      const locationId = draft?.locationId
        || getLocationIdFromGame(game, locationMatchList)
        // Location-less game: land in the synthetic unassigned lane when it's
        // rendered (the `locations` memo appends it whenever such games exist).
        || (grouped[UNASSIGNED_LANE_ID] ? UNASSIGNED_LANE_ID : null);
      if (!locationId) return;
      if (!grouped[locationId]) grouped[locationId] = [];
      grouped[locationId].push(game);
    });

    Object.keys(grouped).forEach((locationId) => {
      grouped[locationId].sort((a, b) => parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time));
    });

    return grouped;
  }, [gamesForDay, locations, locationMatchList, draftByGameId]);

  const timeSlotRangesByLocation = useMemo(() => {
    const ranges: Record<string, { start: number; end: number }[]> = {};

    timeSlots.forEach((slot) => {
      const slotDateKey = toDateKey(slot.availableDate);
      if (slotDateKey !== selectedDateKey) return;

      const locationId = helpers.getObjId(slot.location);
      if (!locationId) return;

      if (!ranges[locationId]) ranges[locationId] = [];

      ranges[locationId].push({
        start: parseTimeToMinutes(slot.startTime),
        end: parseTimeToMinutes(slot.endTime),
      });
    });

    return ranges;
  }, [selectedDateKey, timeSlots]);

  const layoutByLocation = useMemo(() => {
    const layout: Record<string, { items: any[]; groups: any[] }> = {};
    locations.forEach((location) => {
      const locationGames = gamesByLocation[location._id] || [];
      layout[location._id] = buildOverlapLayout(locationGames, draftByGameId, defaultDuration);
    });
    return layout;
  }, [locations, gamesByLocation, draftByGameId, defaultDuration]);

  const commitDraftUpdate = useCallback(async (gameId: string, start: number, duration: number, locationIdOverride?: string) => {
    const game = allTournamentGames.find((g) => g._id === gameId);
    if (!game) return;

    const laneId = locationIdOverride || getLocationIdFromGame(game, locationMatchList);
    // Synthetic lane ids (unassigned / name:<string>) must never be written
    // into the `locationModel` ObjectId ref — dropping into those lanes only
    // moves the game in time and leaves its location fields untouched.
    const locationId = isRealLocationId(laneId) ? laneId : null;
    const matchedTimeSlot = locationId
      ? getTimeSlotForGame(timeSlots, selectedDateKey, locationId, start)
      : null;

    const updatedGame = {
      ...game,
      time: formatMinutesForGameTime(start),
      duration,
      globalDate: toGlobalDate(selectedDateKey),
      date: toGlobalDate(selectedDateKey),
      timeSlot: matchedTimeSlot?._id,
      locationModel: locationId || helpers.getObjId(game.locationModel),
      location: (locationMatchList.find((l) => l._id === locationId)?.name) || game.location,
    };
    if (updatedGame.playoffRound == null) {
      updatedGame.playoffRound = '';
    }

    await updateGame(updatedGame);
    await loadAllTournamentGames(seasons);
  }, [allTournamentGames, locationMatchList, selectedDateKey, timeSlots, updateGame, loadAllTournamentGames, seasons]);

  useEffect(() => {
    draftByGameIdRef.current = draftByGameId;
  }, [draftByGameId]);

  useEffect(() => {
    if (!interaction) return;

    const onMove = (event: MouseEvent) => {
      const deltaY = event.clientY - interaction.startClientY;
      const deltaSlots = Math.round(deltaY / SLOT_HEIGHT);
      const deltaMinutes = deltaSlots * SNAP_MINUTES;

      if (interaction.mode === 'move') {
        const nextStart = clamp(
          interaction.initialStart + deltaMinutes,
          bounds.start,
          bounds.end - interaction.initialDuration,
        );
        let nextLocationId = interaction.initialLocationId;
        if (calendarRef.current && locations.length > 0) {
          const rect = calendarRef.current.getBoundingClientRect();
          const localX = event.clientX - rect.left + calendarRef.current.scrollLeft;
          const columnIndex = Math.floor((localX - TIME_GUTTER_WIDTH) / COLUMN_WIDTH);
          if (columnIndex >= 0 && columnIndex < locations.length) {
            nextLocationId = locations[columnIndex]._id;
          }
        }

        setDraftByGameId((prev) => ({
          ...prev,
          [interaction.gameId]: {
            start: nextStart,
            duration: interaction.initialDuration,
            locationId: nextLocationId,
          },
        }));
      }

      if (interaction.mode === 'resize') {
        const nextDuration = clamp(
          interaction.initialDuration + deltaMinutes,
          SNAP_MINUTES,
          bounds.end - interaction.initialStart,
        );

        setDraftByGameId((prev) => ({
          ...prev,
          [interaction.gameId]: {
            start: interaction.initialStart,
            duration: nextDuration,
          },
        }));
      }
    };

    const onUp = async () => {
      const draft = draftByGameIdRef.current[interaction.gameId];
      setInteraction(null);
      document.body.style.userSelect = '';

      if (!draft) return;

      const didChange =
        draft.start !== interaction.initialStart ||
        draft.duration !== interaction.initialDuration ||
        draft.locationId !== interaction.initialLocationId;

      if (!didChange) {
        setDraftByGameId((prev) => {
          const next = { ...prev };
          delete next[interaction.gameId];
          return next;
        });
        return;
      }

      try {
        await commitDraftUpdate(interaction.gameId, draft.start, draft.duration, draft.locationId);
        toast.success('Game updated');
      } catch (error) {
        toast.error('Failed to update game');
      } finally {
        setDraftByGameId((prev) => {
          const next = { ...prev };
          delete next[interaction.gameId];
          return next;
        });
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
  }, [interaction, bounds.start, bounds.end, commitDraftUpdate, locations]);

  const onStartMove = (event: React.MouseEvent, game: any) => {
    event.preventDefault();
    event.stopPropagation();

    const gameId = game._id;
    const currentStart = draftByGameId[gameId]?.start ?? parseTimeToMinutes(game.time);
    const currentDuration = draftByGameId[gameId]?.duration ?? (game.duration || defaultDuration);
    const currentLocationId = draftByGameId[gameId]?.locationId || getLocationIdFromGame(game, locationMatchList);

    document.body.style.userSelect = 'none';
    setInteraction({
      mode: 'move',
      gameId,
      startClientY: event.clientY,
      initialStart: currentStart,
      initialDuration: currentDuration,
      initialLocationId: currentLocationId,
    });
  };

  const onStartResize = (event: React.MouseEvent, game: any) => {
    event.preventDefault();
    event.stopPropagation();

    const gameId = game._id;
    const currentStart = draftByGameId[gameId]?.start ?? parseTimeToMinutes(game.time);
    const currentDuration = draftByGameId[gameId]?.duration ?? (game.duration || defaultDuration);

    document.body.style.userSelect = 'none';
    setInteraction({
      mode: 'resize',
      gameId,
      startClientY: event.clientY,
      initialStart: currentStart,
      initialDuration: currentDuration,
    });
  };

  const openCreateModal = () => {
    const defaultLocation = locations[0]?._id || '';
    const seasonId = createForm.seasonId || seasons[0]?._id || '';

    setCreateForm({
      seasonId,
      divisionId: '',
      homeTeamId: '',
      awayTeamId: '',
      locationId: defaultLocation,
      startMinutes: 9 * 60,
      duration: defaultDuration,
    });
    setIsCreateOpen(true);
  };

  const onCreateGame = async () => {
    if (!createForm.seasonId || !createForm.divisionId || !createForm.homeTeamId || !createForm.awayTeamId || !createForm.locationId) {
      toast.error('Please complete all required fields');
      return;
    }

    if (createForm.homeTeamId === createForm.awayTeamId) {
      toast.error('Home and away team must be different');
      return;
    }

    const division = divisions.find((d) => d._id === createForm.divisionId);
    const location = locations.find((loc) => loc._id === createForm.locationId);

    if (!division || !location) {
      toast.error('Invalid division or location');
      return;
    }

    const slot = getTimeSlotForGame(timeSlots, selectedDateKey, createForm.locationId, createForm.startMinutes);

    const payload = {
      division: division._id,
      season: helpers.getObjId(division.season) || createForm.seasonId,
      league: helpers.getObjId(division.league),
      homeTeam: createForm.homeTeamId,
      awayTeam: createForm.awayTeamId,
      // `locationModel` is an ObjectId ref to Location; synthetic lanes
      // (unassigned / name:<string>) carry non-id _ids — omit the ref and, for
      // the unassigned lane, the display name too (the game has no location).
      ...(isRealLocationId(createForm.locationId) ? { locationModel: createForm.locationId } : {}),
      ...(location.synthetic ? {} : { location: getLocationDisplayName(location) }),
      globalDate: toGlobalDate(selectedDateKey),
      date: toGlobalDate(selectedDateKey),
      time: formatMinutesForGameTime(createForm.startMinutes),
      timeSlot: slot?._id,
      duration: createForm.duration,
      sport: Array.isArray(division.sports) && division.sports.length > 0 ? division.sports[0] : 'basketball',
    };

    try {
      await addGame(payload);
      await loadAllTournamentGames(seasons);
      toast.success('Game created');
      setIsCreateOpen(false);
    } catch (error) {
      toast.error('Failed to create game');
    }
  };

  const quarterTicks = useMemo(() => {
    const ticks = [];
    for (let minute = bounds.start; minute <= bounds.end; minute += SNAP_MINUTES) {
      ticks.push(minute);
    }
    return ticks;
  }, [bounds.start, bounds.end]);

  const createDivisions = useMemo(() => {
    return divisions.filter((division) => {
      const divisionSeasonId = helpers.getObjId(division.season);
      return !createForm.seasonId || !divisionSeasonId || divisionSeasonId === createForm.seasonId;
    });
  }, [divisions, createForm.seasonId]);

  useEffect(() => {
    if (!createForm.divisionId) return;
    getTeamDivisionsByDivisionId(createForm.divisionId);
  }, [createForm.divisionId]);

  return (
    <PageContainer fullWidth>
      <PageHeader
        title="Tournament Scheduler Day View"
        description="Google Calendar-style day scheduler by location"
        actions={
          <>
            <Link to={`/admin/scheduler-day2/tournament/${tournamentId}/bulk-import`}>
              <Button variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Bulk import
              </Button>
            </Link>
            <Button variant={timelessGames.length ? 'default' : 'outline'} onClick={() => setQuickOpen(true)} data-tour="scheduler-quick-availability">
              <CalendarDays className="h-4 w-4 mr-2" />
              {timelessGames.length ? `Set game times (${timelessGames.length})` : 'Set up availability'}
            </Button>
            <Button onClick={openCreateModal} disabled={!selectedDateKey || locations.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Create Game
            </Button>
          </>
        }
      />
      {tournamentId && (
        <QuickAvailabilityWizard
          open={quickOpen}
          onOpenChange={setQuickOpen}
          organization={organization}
          competition={{ kind: 'tournament', id: tournamentId, name: 'This tournament' }}
          timelessGames={timelessGames}
          onDone={() => { getTimeSlotsByTournamentId(tournamentId); loadAllTournamentGames(seasons); }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Select day and optional section highlight</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Highlight Section</Label>
            <Select
              value={highlightSeasonId}
              onValueChange={(value) => {
                setHighlightSeasonId(value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select section to highlight" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sections</SelectItem>
                {seasons.map((season) => (
                  <SelectItem key={season._id} value={season._id}>{season.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Date</Label>
            {allDateKeys.length === 0 ? (
              <div className="text-sm text-muted-foreground">No dates available</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {allDateKeys.map((key) => {
                  const isActive = key === selectedDateKey;
                  return (
                    <Button
                      key={key}
                      type="button"
                      variant={isActive ? 'default' : 'outline'}
                      onClick={() => setSelectedDateKey(key)}
                      className="h-auto py-2 px-3 text-left"
                    >
                      {formatDateForSelect(key)}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {locations.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground mb-3">No availability for this date yet. Tell us when you play and the day view turns on.</p>
            <div className="flex flex-wrap justify-center gap-3 items-center">
              <Button onClick={() => setQuickOpen(true)} data-tour="scheduler-quick-availability-empty">
                <CalendarDays className="h-4 w-4 mr-2" />
                {timelessGames.length ? 'Set game times' : 'Set up availability'}
              </Button>
              <Link to="/admin/locations" className="text-primary underline text-sm">Manage venues</Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Day View
            </CardTitle>
            <CardDescription>Drag game cards vertically to move start time. Drag bottom handle to resize in 15-minute increments.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <div
              ref={calendarRef}
              className="relative min-w-max border rounded-md bg-card"
              style={{ width: TIME_GUTTER_WIDTH + (locations.length * COLUMN_WIDTH) }}
            >
              <div className="sticky top-0 z-30 flex border-b bg-card">
                <div style={{ width: TIME_GUTTER_WIDTH }} className="border-r px-2 py-2 text-xs font-semibold text-muted-foreground">
                  Time
                </div>
                {locations.map((location) => (
                  <div
                    key={location._id}
                    style={{ width: COLUMN_WIDTH }}
                    className="border-r px-3 py-2 text-sm font-semibold"
                  >
                    {location.synthetic ? location.name : getLocationDisplayName(location)}
                  </div>
                ))}
              </div>

              <div className="relative flex">
                <div style={{ width: TIME_GUTTER_WIDTH, height: totalHeight }} className="border-r bg-muted/50">
                  {quarterTicks.map((minute) => {
                    const top = ((minute - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                    const isHour = minute % 60 === 0;
                    return (
                      <div
                        key={`label-${minute}`}
                        className="absolute left-0 right-0"
                        style={{ top }}
                      >
                        {isHour ? (
                          <span className="absolute -top-2 left-2 text-[11px] text-muted-foreground">{formatTimeLabel(minute)}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                {locations.map((location) => {
                  const slotRanges = timeSlotRangesByLocation[location._id] || [];
                  const layout = layoutByLocation[location._id] || { items: [], groups: [] };

                  return (
                    <div
                      key={location._id}
                      style={{ width: COLUMN_WIDTH, height: totalHeight }}
                      className="relative border-r"
                    >
                      {quarterTicks.map((minute) => {
                        const top = ((minute - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        const isHour = minute % 60 === 0;
                        return (
                          <div
                            key={`line-${location._id}-${minute}`}
                            className={`absolute left-0 right-0 border-t ${isHour ? 'border-input' : 'border-border'}`}
                            style={{ top }}
                          />
                        );
                      })}

                      {slotRanges.map((range, index) => {
                        const start = clamp(range.start, bounds.start, bounds.end);
                        const end = clamp(range.end, bounds.start, bounds.end);
                        if (end <= start) return null;
                        const top = ((start - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        const height = ((end - start) / SNAP_MINUTES) * SLOT_HEIGHT;

                        return (
                          <div
                            key={`range-${location._id}-${index}`}
                            className="absolute left-0 right-0 bg-success/10 pointer-events-none"
                            style={{ top, height }}
                          />
                        );
                      })}

                      {layout.groups.map((group) => {
                        if (!group.hiddenCount) return null;
                        const top = ((group.start - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        return (
                          <div
                            key={`more-${location._id}-${group.start}`}
                            className="absolute right-2 z-20 rounded-full bg-foreground/80 px-2 py-[2px] text-[10px] font-semibold text-background"
                            style={{ top: top + 2 }}
                          >
                            +{group.hiddenCount} more
                          </div>
                        );
                      })}

                      {layout.items.map((item) => {
                        if (item.column >= MAX_VISIBLE_COLUMNS) return null;

                        const game = item.game;
                        const gameId = game._id;
                        const start = item.start;
                        const duration = item.duration;
                        const top = ((start - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        const height = Math.max((duration / SNAP_MINUTES) * SLOT_HEIGHT, SLOT_HEIGHT);

                        const visibleColumns = Math.min(item.columnCount || 1, MAX_VISIBLE_COLUMNS);
                        const availableWidth = COLUMN_WIDTH - (GAME_INSET * 2) - ((visibleColumns - 1) * GAME_GAP);
                        const columnWidth = Math.max(40, availableWidth / visibleColumns);
                        const left = GAME_INSET + item.column * (columnWidth + GAME_GAP);

                        const home = getTeamName(game.homeTeam, teamDivisions);
                        const away = getTeamName(game.awayTeam, teamDivisions);
                        const contestName = seasons.find((s) => s._id === helpers.getObjId(game.season))?.name || '';
                        const divisionName = divisions.find(d => d._id === helpers.getObjId(game.division))?.name || '';
                        const contestDivisionLabel = contestName && divisionName
                          ? `${contestName} / ${divisionName}`
                          : (contestName || divisionName);
                        const gameSeasonId = helpers.getObjId(game.season);
                        const isHighlighted = highlightSeasonId !== 'all' && gameSeasonId === highlightSeasonId;
                        const isDimmed = highlightSeasonId !== 'all' && gameSeasonId !== highlightSeasonId;

                        return (
                          <div
                            key={gameId}
                            className={`absolute rounded-md shadow-sm overflow-hidden ${
                              isHighlighted
                                ? 'border-2 border-warning bg-warning/10'
                                : 'border border-primary bg-primary/10'
                            } ${isDimmed ? 'opacity-45' : 'opacity-100'}`}
                            style={{
                              top,
                              height,
                              minHeight: SLOT_HEIGHT,
                              left,
                              width: columnWidth,
                            }}
                          >
                            <button
                              type="button"
                              className="w-full h-full px-2 pt-1 pb-4 text-left cursor-grab active:cursor-grabbing"
                              onMouseDown={(event) => onStartMove(event, game)}
                            >
                              <div className="text-[11px] font-semibold text-primary-deep truncate">
                                {formatMinutesForGameTime(start)} - {formatMinutesForGameTime(start + duration)}
                              </div>
                              <div className="text-[11px] text-foreground truncate">{home} vs {away}</div>
                              {contestDivisionLabel ? <div className="text-[10px] text-muted-foreground truncate">{contestDivisionLabel}</div> : null}
                            </button>

                            <button
                              type="button"
                              className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize bg-primary/40 hover:bg-primary/70"
                              onMouseDown={(event) => onStartResize(event, game)}
                              aria-label="Resize game duration"
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Game</DialogTitle>
            <DialogDescription>Set section, division, teams, location and time for {formatDateForSelect(selectedDateKey)}.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Section (Season)</Label>
              <Select
                value={createForm.seasonId}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, seasonId: value, divisionId: '', homeTeamId: '', awayTeamId: '' }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map((season) => (
                    <SelectItem key={season._id} value={season._id}>{season.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Division</Label>
              <Select
                value={createForm.divisionId}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, divisionId: value, homeTeamId: '', awayTeamId: '' }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select division" />
                </SelectTrigger>
                <SelectContent>
                  {createDivisions.map((division) => (
                    <SelectItem key={division._id} value={division._id}>{division.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Home Team</Label>
                <Select
                  value={createForm.homeTeamId}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, homeTeamId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select home team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamsForCreateDivision.map((team) => (
                      <SelectItem key={team._id} value={team._id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Away Team</Label>
                <Select
                  value={createForm.awayTeamId}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, awayTeamId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select away team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamsForCreateDivision.map((team) => (
                      <SelectItem key={team._id} value={team._id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Location</Label>
              <Select
                value={createForm.locationId}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, locationId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location._id} value={location._id}>
                      {location.synthetic ? (location.name || 'No location') : getLocationDisplayName(location)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Select
                  value={String(createForm.startMinutes)}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, startMinutes: Number(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select start time" />
                  </SelectTrigger>
                  <SelectContent>
                    {quarterTicks.map((minute) => (
                      <SelectItem key={`start-${minute}`} value={String(minute)}>{formatMinutesForGameTime(minute)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={String(createForm.duration)}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, duration: Number(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {[15, 30, 45, 60, 75, 90, 105, 120].map((value) => (
                      <SelectItem key={`dur-${value}`} value={String(value)}>{value} minutes</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={onCreateGame}>Create Game</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
